import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useEffect, useState } from "react";
import { useTranslation } from "../../../../hooks/useTranslation";
import { dateInputStyles, getDateInputProps } from "../../../../styles/dateInputStyles";
import { toLocalDateString } from "../../../../utils/dateFormat";
import { getModalZIndex } from "../../../../utils/zIndexLayers";

interface BulkAvailabilityModalProps {
    opened: boolean;
    onClose: () => void;
    selectedCount: number;
    onConfirm: (availabilityDate: string | undefined) => Promise<void>;
}

/**
 * Reuses ADR_2026-05-06_02's UI shape only (Mantine date input, empty = clear, confirm shows
 * "Update N people") — not its backend design, which targeted now-obsolete Person fields. Uses
 * DateInput (not the ADR's literally-named DatePicker) since that's the actual, currently-shipped
 * convention for this exact single-vs-bulk availability flow (PersonActionsMenu's
 * AssignmentChangeModal, and Employee Overview's own search filters) — same styling
 * (dateInputStyles/getDateInputProps) for visual consistency, not a new pattern.
 */
export function BulkAvailabilityModal({ opened, onClose, selectedCount, onConfirm }: BulkAvailabilityModalProps) {
    const { t } = useTranslation();
    const [date, setDate] = useState<Date | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const dateInputBaseProps = getDateInputProps();

    useEffect(() => {
        if (opened) {
            setDate(null);
        }
    }, [opened]);

    const handleConfirm = async () => {
        setIsSaving(true);
        try {
            await onConfirm(date ? toLocalDateString(date) : undefined);
        } finally {
            setIsSaving(false);
        }
    };

    const isSingular = selectedCount === 1;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t("EMPLOYEEOVERVIEW_BULK_SET_AVAILABILITY_BUTTON", undefined, "Set availability")}
            size="sm"
            centered
            withCloseButton
            closeOnClickOutside={!isSaving}
            zIndex={getModalZIndex()}
        >
            <Stack>
                <DateInput
                    label={t("EMPLOYEEOVERVIEW_BULK_MODAL_DATE_LABEL", undefined, "Availability date")}
                    placeholder={t("EMPLOYEEOVERVIEW_BULK_MODAL_DATE_PLACEHOLDER", undefined, "Leave empty to clear availability")}
                    value={date}
                    onChange={setDate}
                    styles={dateInputStyles}
                    disabled={isSaving}
                    {...dateInputBaseProps}
                />
                <Text size="xs" c="dimmed">
                    {isSingular
                        ? t("EMPLOYEEOVERVIEW_BULK_MODAL_HELPER_SINGULAR", undefined, "Leave empty to clear availability for the selected person.")
                        : t("EMPLOYEEOVERVIEW_BULK_MODAL_HELPER_PLURAL", undefined, "Leave empty to clear availability for the selected people.")}
                </Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        {t("EMPLOYEEOVERVIEW_BULK_CANCEL_BUTTON", undefined, "Cancel")}
                    </Button>
                    <Button onClick={handleConfirm} loading={isSaving}>
                        {isSingular
                            ? t("EMPLOYEEOVERVIEW_BULK_MODAL_CONFIRM_SINGULAR", { count: selectedCount.toLocaleString() }, `Update ${selectedCount.toLocaleString()} person`)
                            : t("EMPLOYEEOVERVIEW_BULK_MODAL_CONFIRM_PLURAL", { count: selectedCount.toLocaleString() }, `Update ${selectedCount.toLocaleString()} people`)}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
